import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Input";

// Self-serve password change for the signed-in user. Requires the
// current password (verified server-side against Supabase Auth) plus
// a new password + confirmation. On success the session's tokens
// stay valid — Supabase re-issues them silently as part of
// updateUser — so no re-login is needed.
//
// Below the change form we also expose a "Forgot your current
// password?" escape hatch that mails a reset link to the user's own
// address — matches the Login page's forgot-password flow but works
// even while signed in.
export function ChangePasswordPanel() {
  const user = useAuth((s) => s.user);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState<string | null>(null);

  const changeMut = useMutation({
    mutationFn: () => api.changePassword({
      currentPassword: current,
      newPassword: next,
    }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      setFlash("Password changed. Your new password is active on this device now.");
    },
    onError: (e: { message?: string }) => {
      setFlash(null);
      setError(e?.message ?? "Couldn't change password.");
    },
  });

  const resetMut = useMutation({
    mutationFn: () => api.sendPasswordResetToSelf(),
    onSuccess: (email) => {
      setError(null);
      setFlash(null);
      setResetSent(email);
    },
    onError: (e: { message?: string }) => {
      setError(e?.message ?? "Couldn't send reset email.");
    },
  });

  if (!user) return null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFlash(null);
    if (next !== confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    changeMut.mutate();
  }

  const canSubmit =
    !changeMut.isPending
    && current.length > 0
    && next.length >= 8
    && confirm.length >= 8;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Change password</h2>
        <p className="mt-1 text-xs text-slate-500">
          Enter your current password to confirm it's you, then pick a
          new one. Minimum 8 characters.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="Current password">
          <Input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            disabled={changeMut.isPending}
          />
        </Field>
        <Field label="New password">
          <Input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            disabled={changeMut.isPending}
          />
        </Field>
        <Field label="Confirm new password">
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={changeMut.isPending}
          />
        </Field>

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}
        {flash && (
          <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {flash}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={!canSubmit}>
            {changeMut.isPending ? "Changing…" : "Change password"}
          </Button>
        </div>
      </form>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs text-slate-600 mb-2">
          Forgot your current password? We can email a reset link to{" "}
          <strong>{user.email}</strong> — click the link inside and set
          a new password without needing the old one.
        </div>
        {resetSent ? (
          <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            Reset link sent to <strong>{resetSent}</strong>. Check your
            inbox (and spam folder).
          </div>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => resetMut.mutate()}
            disabled={resetMut.isPending}
          >
            {resetMut.isPending ? "Sending…" : "Email me a reset link"}
          </Button>
        )}
      </div>
    </section>
  );
}
